/**
 * Print Bridge — hardware-agnostic abstraction.
 *
 * Browser/PWA tarafında Web Bluetooth / WebUSB veya Capacitor plugin'i
 * (Sunmi/PAX SDK) üzerinden cihaza ESC/POS bytes yollar.
 *
 * Strateji:
 * 1. Capacitor mode: native plugin'ler kullanılır (Sunmi Printer, PAX SDK)
 * 2. Browser mode: WebBluetooth (Star/Bixolon) veya Web Serial API
 * 3. Backend proxy mode: backend → printer LAN direkt yazar (fallback)
 *
 * Cihaz tarafı Capacitor app her 3sn'de bir pending-jobs poll'lar veya
 * WS dinler; gelen bir job'u render edip vendor SDK ile basar.
 */

import { apiClient } from "../api";

export interface PrintJob {
  id: string;
  device: string;
  device_name: string;
  job_type: string;
  reference_type: string;
  reference_id: string;
  escpos_b64: string | null;
  html_payload: string;
  metadata: Record<string, any>;
  status: string;
  attempts: number;
}

export interface PrintBridge {
  name: string;
  isAvailable(): Promise<boolean>;
  print(payload: Uint8Array, opts?: { device?: any }): Promise<void>;
}

/**
 * Capacitor + Sunmi inner printer (Android, Sunmi V2/P2/T3).
 * Plugin: capacitor-plugin-sunmi-printer veya custom.
 */
class SunmiPrintBridge implements PrintBridge {
  name = "sunmi";

  async isAvailable(): Promise<boolean> {
    // @ts-ignore - Capacitor global
    const w = window as any;
    if (!w.Capacitor || w.Capacitor.platform !== "android") return false;
    return !!w.SunmiPrinter || !!w.Capacitor.Plugins?.SunmiPrinter;
  }

  async print(payload: Uint8Array): Promise<void> {
    const w = window as any;
    const plugin = w.SunmiPrinter || w.Capacitor.Plugins.SunmiPrinter;
    // Sunmi SDK çoğunlukla "sendRawData" alır
    await plugin.sendRawData({ data: Array.from(payload) });
    await plugin.printerCut?.({ mode: "full" });
  }
}

/**
 * PAX A920 / A80 — Broadpos veya PAX SDK üstünden.
 */
class PaxPrintBridge implements PrintBridge {
  name = "pax";

  async isAvailable(): Promise<boolean> {
    const w = window as any;
    return !!w.PaxPosLink || !!w.Capacitor?.Plugins?.PaxPrinter;
  }

  async print(payload: Uint8Array): Promise<void> {
    const w = window as any;
    const plugin = w.PaxPosLink || w.Capacitor.Plugins.PaxPrinter;
    await plugin.printRaw({ data: Array.from(payload) });
  }
}

/**
 * Web Bluetooth — Star TSP143IIIBI gibi BT yazıcılar.
 * Browser tarafı, Capacitor olmadan da çalışır.
 */
class WebBluetoothPrintBridge implements PrintBridge {
  name = "web-bluetooth";
  device: any = null;

  async isAvailable(): Promise<boolean> {
    return typeof navigator !== "undefined" && "bluetooth" in navigator;
  }

  async pair(): Promise<void> {
    // @ts-ignore
    this.device = await navigator.bluetooth.requestDevice({
      filters: [{ services: [0x1101] }, { namePrefix: "Star" }, { namePrefix: "TSP" }],
      optionalServices: ["00001101-0000-1000-8000-00805f9b34fb"],
    });
  }

  async print(payload: Uint8Array): Promise<void> {
    if (!this.device) await this.pair();
    const server = await this.device.gatt.connect();
    const service = await server.getPrimaryService("00001101-0000-1000-8000-00805f9b34fb");
    const characteristic = await service.getCharacteristic("00001101-0000-1000-8000-00805f9b34fb");
    // BLE MTU sınırı 512 byte; chunk'la gönder
    const CHUNK = 256;
    for (let i = 0; i < payload.length; i += CHUNK) {
      await characteristic.writeValue(payload.slice(i, i + CHUNK));
    }
    await server.disconnect();
  }
}

/**
 * Backend proxy — backend doğrudan TCP printer'a yazsın.
 * En basit fallback, mutfak yazıcısı için ideal (LAN'da statik IP).
 */
class BackendProxyPrintBridge implements PrintBridge {
  name = "backend-proxy";

  async isAvailable(): Promise<boolean> {
    return true; // her zaman fallback
  }

  async print(_payload: Uint8Array): Promise<void> {
    // Backend zaten print-job'u oluştururken kendi tarafından
    // (Celery worker veya signal) printer'a yazar. Client tarafı
    // sadece job'u mark-printing/mark-done ile günceller.
  }
}

const BRIDGES: PrintBridge[] = [
  new SunmiPrintBridge(),
  new PaxPrintBridge(),
  new WebBluetoothPrintBridge(),
  new BackendProxyPrintBridge(),
];

/**
 * Mevcut platforma uygun ilk bridge'i seç.
 */
export async function resolveBridge(): Promise<PrintBridge> {
  for (const b of BRIDGES) {
    if (await b.isAvailable()) return b;
  }
  return new BackendProxyPrintBridge();
}

function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Tek bir print job'u çalıştır — payload'ı bridge üzerinden cihaza yolla,
 * sonucu backend'e raporla.
 */
export async function executePrintJob(job: PrintJob): Promise<void> {
  if (!job.escpos_b64) {
    await apiClient.patch(`/api/v1/print-jobs/${job.id}/mark-done/`, {
      success: false,
      error: "escpos payload missing",
    });
    return;
  }

  await apiClient.patch(`/api/v1/print-jobs/${job.id}/mark-printing/`, {});

  try {
    const bridge = await resolveBridge();
    const bytes = b64ToBytes(job.escpos_b64);
    await bridge.print(bytes);
    await apiClient.patch(`/api/v1/print-jobs/${job.id}/mark-done/`, { success: true });
  } catch (e: any) {
    await apiClient.patch(`/api/v1/print-jobs/${job.id}/mark-done/`, {
      success: false,
      error: e?.message || String(e),
    });
    throw e;
  }
}

/**
 * Periodik poller — POS Mobile / KDS app'inin background'unda çalışır,
 * her 3sn'de pending-jobs çeker ve sırayla basar.
 */
export function startPrintQueueWorker(deviceId: string, intervalMs: number = 3000) {
  let stopped = false;
  let running = false;

  async function tick() {
    if (stopped || running) return;
    running = true;
    try {
      const { data } = await apiClient.get(`/api/v1/hardware-devices/${deviceId}/pending-jobs/`);
      const jobs: PrintJob[] = data || [];
      for (const job of jobs) {
        if (stopped) break;
        try {
          await executePrintJob(job);
        } catch {
          // Hata logla, kuyrukta devam
        }
      }
    } catch {
      // Network hatası — bir sonraki tick'te tekrar denenecek
    } finally {
      running = false;
    }
  }

  const interval = setInterval(tick, intervalMs);
  tick(); // ilk anında bir kez

  return () => {
    stopped = true;
    clearInterval(interval);
  };
}

/**
 * Cihaz heartbeat — her 30 sn'de backend'e durumu bildir.
 */
export function startHeartbeat(deviceId: string, intervalMs: number = 30000) {
  let stopped = false;
  async function beat() {
    if (stopped) return;
    try {
      await apiClient.post(`/api/v1/hardware-devices/${deviceId}/heartbeat/`, {
        status: navigator.onLine ? "ONLINE" : "OFFLINE",
      });
    } catch {
      // ignore
    }
  }
  beat();
  const interval = setInterval(beat, intervalMs);
  return () => {
    stopped = true;
    clearInterval(interval);
  };
}
